import React, { useState, useEffect } from 'react';
import {
  Container,
  Button,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  Divider,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import {
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  CalendarToday as CalendarIcon,
  Schedule as ScheduleIcon,
  AccessTime as TimeClockIcon,
  Settings as SettingsIcon,
  Payments as PaymentsIcon,
} from '@mui/icons-material';
import { NavLink, useNavigate } from 'react-router-dom';
import { canAccessFeature, normalizePermissionLevel } from '../utils/permissions';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import PersonIcon from '@mui/icons-material/Person';
import HelpIcon from '@mui/icons-material/Help';
import LogoutIcon from '@mui/icons-material/Logout';
import { FormControlLabel, Switch, ListItemIcon as MUIListItemIcon, Typography } from '@mui/material';

const LINKS = [
  { to: '/dashboard', label: 'Dashboard', icon: <DashboardIcon />, feature: 'dashboard_admin' },
  { to: '/employee-central', label: 'Employees', icon: <PeopleIcon />, feature: 'employee_central' },
  { to: '/schedule-creation', label: 'Create Schedule', icon: <CalendarIcon />, feature: 'schedule_creation' },
  { to: '/employee-availability', label: 'Availability', icon: <ScheduleIcon />, feature: 'employee_availability' },
  { to: '/time-clock', label: 'Time Clock', icon: <TimeClockIcon />, feature: 'time_clock_admin' },
  { to: '/time-entries', label: 'Timesheets', icon: <TimeClockIcon />, feature: 'time_entries' },
  { to: '/payroll', label: 'Payroll', icon: <PaymentsIcon />, feature: 'payroll' },
  { to: '/settings', label: 'Settings', icon: <SettingsIcon />, feature: 'settings_central' },
];

export default function TopNavLinks({ darkMode, onToggleDarkMode }) {
  const [open, setOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const adminUser = typeof window !== 'undefined' ? localStorage.getItem('adminUser') : null;
    if (adminUser) {
      try { setUser(JSON.parse(adminUser)); } catch (e) { setUser(null); }
    }
  }, []);

  const hasTenant = Boolean(user);
  if (!hasTenant) return null;
  const currentPermission = normalizePermissionLevel(user?.permissionLevel);

  const handleMenuOpen = (e) => setAnchorEl(e.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);
  const handleLogout = () => {
    try { localStorage.removeItem('adminUser'); } catch (e) {}
    // refresh theme/user state if the global helper exists
    if (window.reloadUserTheme) window.reloadUserTheme();
    handleMenuClose();
    navigate('/login');
  };

  const getUserInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0,2);
  };
  const visibleLinks = LINKS.filter((link) => canAccessFeature(currentPermission, link.feature));

  return (
    <>
  <Box sx={{
    width: '100%',
    backgroundColor: '#39385c',
    color: 'common.white',
    '--top-nav-height': '64px',
    '--nav-height': 'var(--top-nav-height)',
    display: 'flex',
    alignItems: 'center',
    height: 'var(--nav-height)',
    position: 'sticky',
  top: 'var(--app-top)',
  zIndex: (theme) => theme.zIndex.appBar + 1500,
  }}>
  <Container
          maxWidth="lg"
          sx={{
      height: '100%',
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            py: 0,
            px: { xs: 1, md: 0 },
            color: 'inherit',
          }}
        >
        {/* Hamburger: visible on small screens */}
        <IconButton
          aria-label="Open menu"
          onClick={() => setOpen(true)}
          sx={{ display: { xs: 'inline-flex', md: 'none' }, color: 'common.white' }}
          size="large"
        >
          <MenuIcon sx={{ color: 'inherit' }} />
        </IconButton>

  {/* Desktop links: hidden on xs */}
  <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 1 }}>
          {visibleLinks.map((l) => (
            <Button
              key={l.to}
              component={NavLink}
              to={l.to}
              startIcon={l.icon}
              sx={{
                textTransform: 'none',
                color: 'common.white',
                '& .MuiButton-startIcon': { color: 'common.white' },
                px: 1.5,
                height: '40px',
                borderRadius: 1,
                '&.active': {
                  color: 'common.white',
                  fontWeight: 700,
                  borderBottom: (theme) => `3px solid ${theme.palette.primary.light}`,
                  pb: '6px',
                },
                '&:focus-visible': {
                  outline: '3px solid',
                  outlineColor: (theme) => theme.palette.action.focus,
                  outlineOffset: '2px',
                },
              }}
            >
              {l.label}
            </Button>
          ))}
        </Box>

        {/* spacer to push avatar+logo to right */}
        <Box sx={{ flexGrow: 1 }} />

        {/* avatar - placed between links and logo */}
        <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', mr: { xs: 0.5, md: 1 } }}>
          <IconButton onClick={handleMenuOpen} sx={{ p: 0, ml: 1 }} aria-label="User menu">
            <Avatar sx={{ bgcolor: 'secondary.main' }}>{getUserInitials(user?.name)}</Avatar>
          </IconButton>
          <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose} PaperProps={{ sx: { width: 250, mt: 1 } }}>
            {user && (
              <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>{user.name}</Typography>
                <Typography variant="body2" color="text.secondary">{user.email}</Typography>
                <Typography variant="caption" color="text.secondary">{(user.permissionLevel || '').toString()}</Typography>
              </Box>
            )}

            <MenuItem component={NavLink} to={`/employees/${user?.id || ''}`} onClick={handleMenuClose}>
              <MUIListItemIcon><PersonIcon fontSize="small" /></MUIListItemIcon>
              My Profile
            </MenuItem>

            {(canAccessFeature(currentPermission, 'dashboard_admin') || canAccessFeature(currentPermission, 'schedule_creation') ? (
              <MenuItem component={NavLink} to="/employee/login" onClick={handleMenuClose}>
                <MUIListItemIcon><SwapHorizIcon fontSize="small" /></MUIListItemIcon>
                Employee Dashboard
              </MenuItem>
            ) : null)}

            <Divider />

            <MenuItem>
              <MUIListItemIcon>{darkMode ? <LightModeIcon fontSize="small" /> : <DarkModeIcon fontSize="small" />}</MUIListItemIcon>
              <FormControlLabel
                control={<Switch checked={Boolean(darkMode)} onChange={onToggleDarkMode} size="small" />}
                label={darkMode ? 'Light Mode' : 'Dark Mode'}
                sx={{ ml: 0, mr: 0 }}
              />
            </MenuItem>

            <Divider />

            {(canAccessFeature(currentPermission, 'time_entries') ? (
              <MenuItem component={NavLink} to="/time-entries" onClick={handleMenuClose}>
                <MUIListItemIcon><AccessTimeIcon fontSize="small" /></MUIListItemIcon>
                Time Entries
              </MenuItem>
            ) : null)}

            {(canAccessFeature(currentPermission, 'settings_central') ? (
              <MenuItem component={NavLink} to="/settings" onClick={handleMenuClose}>
                <MUIListItemIcon><SettingsIcon fontSize="small" /></MUIListItemIcon>
                Settings
              </MenuItem>
            ) : null)}

            <MenuItem onClick={() => { handleMenuClose(); window.open('https://help.shiftworks.com', '_blank'); }}>
              <MUIListItemIcon><HelpIcon fontSize="small" /></MUIListItemIcon>
              Help & Support
            </MenuItem>

            <Divider />

            <MenuItem onClick={handleLogout}>
              <MUIListItemIcon><LogoutIcon fontSize="small" /></MUIListItemIcon>
              Logout
            </MenuItem>
          </Menu>
        </Box>

        {/* logo aligned right - slightly smaller */}
        <Box sx={{ display: 'flex', alignItems: 'center', mr: { xs: 0.5, md: 1 } }}>
          <Box
            component="img"
            src={`${process.env.PUBLIC_URL}/LogoNew.svg`}
            alt="Company logo"
              sx={{
                height: { xs: 'calc(var(--nav-height) * 0.45)', sm: 'calc(var(--nav-height) * 0.6)', md: 'calc(var(--nav-height) * 0.7)' },
                maxHeight: 'calc(var(--nav-height) - 10px)',
                width: 'auto',
                objectFit: 'contain',
                display: { xs: 'none', sm: 'block' },
              }}
          />
        </Box>
        </Container>
      </Box>

      <Drawer anchor="left" open={open} onClose={() => setOpen(false)}>
  <Box sx={{ width: { xs: '100vw', sm: 360, md: 280 }, bgcolor: '#39385c', color: 'common.white' }} role="presentation" onKeyDown={() => setOpen(false)}>
          <List>
            {visibleLinks.map((l) => (
              <ListItemButton
                key={l.to}
                component={NavLink}
                to={l.to}
                onClick={() => setOpen(false)}
                sx={{
                  color: 'common.white',
                  '& .MuiListItemIcon-root': { color: 'common.white' },
                  '&.active': {
                    bgcolor: 'action.selected',
                    borderLeft: (theme) => `4px solid ${theme.palette.primary.main}`,
                  },
                  '&:focus-visible': {
                    bgcolor: 'action.focus',
                  },
                }}
              >
                <ListItemIcon>{l.icon}</ListItemIcon>
                <ListItemText primary={l.label} primaryTypographyProps={{ sx: { color: 'common.white' } }} />
              </ListItemButton>
            ))}
          </List>
          <Divider />
        </Box>
      </Drawer>
    </>
  );
}
